import React from 'react';
import { PhoneOff, CheckCircle2, Clock } from 'lucide-react';
import Header from '../components/Header';
import OptOutSection from '../components/OptOutSection';
import FAQSection from '../components/FAQSection';
import ContactSection from '../components/ContactSection';
import Footer from '../components/Footer';

const Index: React.FC = () => {
  const faqs = [
    {
      question: "How long does it take to process my opt-out request?",
      answer: "Your opt-out request will be processed within 10 business days. You may continue to receive messages that were scheduled before your request was processed."
    },
    {
      question: "Will I stop receiving all messages immediately?",
      answer: "You may continue to receive messages that were scheduled before your opt-out request. Additionally, transactional and service messages may still be sent if they're related to your account or service."
    },
    {
      question: "Can I opt back in later?",
      answer: "Yes, you can opt back in to receive SMS messages by contacting our customer service team."
    },
    {
      question: "Will this affect other communication channels?",
      answer: "No, opting out of SMS messages will not affect other communication channels like email or postal mail."
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-white to-optout-gray">
      <Header />
      
      <main className="flex-1 flex flex-col">
        {/* Hero Section */}
        <section className="relative px-4 py-16 md:py-24">
          <div className="container mx-auto max-w-5xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div className="order-2 md:order-1 animate-fade-in">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="bg-optout-lightblue h-6 rounded-full px-3 py-1">
                    <span className="text-xs font-medium text-optout-blue">Simple Process</span>
                  </div>
                </div>
                
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-optout-text mb-4 leading-tight text-balance">
                  Stop Receiving SMS Marketing Messages
                </h1>
                
                <p className="text-optout-lighttext mb-8 text-balance">
                  Complete our simple opt-out process to stop receiving SMS marketing messages.
                  Your privacy is important to us, and we make it easy to manage your communication preferences.
                </p>
                
                <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                  <a 
                    href="#opt-out-form" 
                    className="btn-primary px-6 py-3 rounded-lg text-center inline-block"
                  >
                    Opt Out Now
                  </a>
                  
                  <a 
                    href="#faq" 
                    className="px-6 py-3 rounded-lg text-center inline-block border border-optout-blue text-optout-blue hover:bg-optout-lightblue transition-all duration-300"
                  >
                    Learn More
                  </a>
                </div>
              </div>
              
              <div className="order-1 md:order-2 flex justify-center md:justify-end animate-fade-in">
                <div className="relative hidden md:block">
                  <div className="absolute inset-0 bg-optout-blue rounded-3xl opacity-5 blur-xl transform -rotate-6"></div>
                  <div className="glass-card rounded-3xl overflow-hidden p-4 relative z-10">
                    <div className="bg-white rounded-2xl p-6 shadow-sm">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-2">
                          <PhoneOff className="h-5 w-5 text-optout-blue" />
                          <span className="font-medium text-optout-text">SMS Opt-Out</span>
                        </div>
                        <div className="bg-green-100 rounded-full px-2 py-1">
                          <span className="text-xs font-medium text-green-600">Simple</span>
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="flex items-center space-x-3">
                          <div className="bg-optout-gray p-2 rounded-full">
                            <CheckCircle2 className="h-4 w-4 text-optout-blue" />
                          </div>
                          <span className="text-sm text-optout-text">Enter your phone number</span>
                        </div>
                        
                        <div className="flex items-center space-x-3">
                          <div className="bg-optout-gray p-2 rounded-full">
                            <CheckCircle2 className="h-4 w-4 text-optout-blue" />
                          </div>
                          <span className="text-sm text-optout-text">Submit your request</span>
                        </div>
                        
                        <div className="flex items-center space-x-3">
                          <div className="bg-optout-gray p-2 rounded-full">
                            <CheckCircle2 className="h-4 w-4 text-optout-blue" />
                          </div>
                          <span className="text-sm text-optout-text">Receive confirmation</span>
                        </div>
                        
                        <div className="flex items-center space-x-3">
                          <div className="bg-optout-gray p-2 rounded-full">
                            <Clock className="h-4 w-4 text-optout-lighttext" />
                          </div>
                          <span className="text-sm text-optout-lighttext">Processing within 10 days</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        <OptOutSection />
        <FAQSection faqs={faqs} />
        <ContactSection />
      </main>
      
      <Footer />
    </div>
  );
};

export default Index;
